import React, { FunctionComponent, useEffect, useRef, useState } from 'react';

import classnames from 'classnames';
import { get, isNumber } from 'lodash';
import { Nav, NavItem, NavLink, Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { trackPromise } from 'react-promise-tracker';
import { useLocation } from 'react-router-dom';

import { ActionId } from 'admin/actions/Actions';
import { FeatureFlagId, isFeatureFlagEnabled } from 'admin/feature-flags/FeatureFlags';
import { ModuleId } from 'admin/modules/Modules';
import { hasModule } from 'admin/tenants/Tenants';
import { getUser, getSkillJarLink } from 'admin/users/Users';
import { areModulesEnabled, isAuthorized, Profile, SUPER_AGENCY_ID, useProfile, UserType } from 'auth/Auth';
import { AppLink } from 'common/app-link/AppLink';
import { BetaLabel } from 'common/beta-label/BetaLabel';
import { NavTab, Tab } from 'common/chrome/left-nav/nav-tabs/NavTabs.types';
import { LocaleCode } from 'core/util/Locale';
import { Route } from 'core/util/Route';
import { getReviewsDefaultFeedKey } from 'reviews2/feed/Reviews.store';
import { getDepartmentHealthSupport } from 'score/ScoreMetrics';

import 'common/chrome/left-nav/nav-tabs/NavTabs.scss';

interface NavTabHtmlProps {
    name: string;
    beta?: boolean;
}

let supportsDepartments = false;

const NavTabHtml: FunctionComponent<NavTabHtmlProps> = (props) => {
    const { t } = useTranslation(['common/chrome']);
    const { name, beta } = props;

    return (
        <span id={`left-nav-link-${name}`} className="r4e-left-nav-tab-html name">
            {t(name)} {beta && <BetaLabel />}
        </span>
    );
};

const NavTabString: FunctionComponent<NavTabHtmlProps> = (props) => {
    const { t } = useTranslation(['common/chrome']);
    const { name, beta } = props;
    const tooltip = beta ? `${t(name)} (${t('BETA')})` : t(name);

    return <>{tooltip}</>;
};

const getReviewsPath = (profile: Profile): string => {
    const feedKey = getReviewsDefaultFeedKey(profile);
    let storedFeed = localStorage.getItem(feedKey);
    const appUrl = `${window.location.protocol}//${window.location.host}`;

    try {
        storedFeed = JSON.parse(storedFeed!);
    } catch (e) {
        console.error('Error parsing storedFeed', e);
    }

    if (!storedFeed) {
        if (isAuthorized({ actions: [ActionId.Reviews.Unified] })) {
            localStorage.setItem(feedKey, `"${appUrl}/reviews2"`);
            return Route.Reviews2;
        }

        localStorage.setItem(feedKey, `"${appUrl}/reviews"`);
        return Route.Reviews;
    }

    if (/\/reviews3$/.test(storedFeed) || isAuthorized({ featureFlags: [FeatureFlagId.Reviews3Notification] })) {
        localStorage.setItem(feedKey, `"${appUrl}/reviews3"`);
        return Route.Reviews3;
    }

    if (/\/reviews2$/.test(storedFeed)) {
        return Route.Reviews2;
    }

    return Route.Reviews;
};

const getNavTabs = (profile: Profile): NavTab[] => {
    const reviewsPath = getReviewsPath(profile);
    const hasScore2 = isAuthorized({ featureFlags: [FeatureFlagId.ReputationScore] });
    const { tenant } = profile;

    return [
        {
            name: 'REPUTATION_IQ',
            path: `${Route.Social2}/listening-nuvi?route=rep-iq&domain=${document.domain}`,
            icon: 'icon-rep-iq',
            auth: (() => {
                if (!isAuthorized({ actions: [ActionId.Reviews.View] })) {
                    return { actions: [ActionId.Reviews.View] };
                }
                return { actions: [ActionId.RepIQReviews.View] };
            })(),
            userTypes: [UserType.TenantUser],
        },
        {
            name: 'HOME',
            path: `${Route.CommandCenter}`,
            icon: 'icon-home',
            auth: {
                featureFlags: [FeatureFlagId.NovaCommandCenter],
            },
            userTypes: [UserType.TenantUser],
        },
        {
            name: 'REPORTS',
            path: `${Route.UnifiedReporting}`,
            // icon: 'icon-file-reports', todo: uncomment this when the issue can be resolved regarding it occasionally not displaying
            icon: 'icon-file-alt',
            beta: true,
            auth: {
                featureFlags: [FeatureFlagId.NovaCommandCenter],
            },
            userTypes: [UserType.TenantUser],
        },
        {
            name: 'DASHBOARDS',
            path: Route.Dashboards,
            icon: 'icon-tachometer-fast',
            auth: {
                actions: [ActionId.PlatformDashboards.View],
            },
            userTypes: [UserType.TenantUser],
            subtabs: (() => {
                if (
                    isAuthorized({
                        featureFlags: [FeatureFlagId.Reports2],
                    })
                ) {
                    return [
                        {
                            name: 'DASHBOARDS',
                            path: Route.Dashboards,
                            icon: 'icon-tachometer-fast',
                            auth: {
                                actions: [ActionId.PlatformDashboards.View],
                            },
                        },
                        {
                            name: 'DASHBOARDS_2',
                            path: Route.Dashboards2,
                            icon: 'icon-tachometer-fast',
                            auth: {
                                actions: [ActionId.PlatformDashboards.View],
                            },
                            beta: true,
                        },
                    ];
                }
                return undefined;
            })(),
        },
        {
            name: 'COMPETITIVE_INSIGHTS',
            path: Route.CompetitiveInsights,
            icon: 'icon-winner',
            auth: (() => {
                // Logic: auth property does OR of permissions. Adding this IIFE to mimic ANDing of permissions since both the feature flag and action have to evaluate to true.
                if (!isAuthorized({ featureFlags: [FeatureFlagId.CompetitiveInsightsV3] })) {
                    return { featureFlags: [FeatureFlagId.CompetitiveInsightsV3] };
                }
                return { actions: [ActionId.CompetitiveInsightsBasic.View] };
            })(),
            userTypes: [UserType.TenantUser],
        },
        {
            name: 'RATINGS_&_REVIEWS',
            path: reviewsPath,
            icon: 'icon-star',
            auth: {
                actions: [ActionId.Reviews.View],
            },
            userTypes: [UserType.TenantUser],
        },
        {
            name: 'PRESENCE',
            path: Route.Listings,
            icon: 'icon-building',
            auth: {
                actions: [
                    ActionId.AnalyticTrafficInsights.View,
                    ActionId.DirectoryProfiles.View,
                    ActionId.DirectoryListings.View,
                    ActionId.DirectoryProfiles.View,
                    ActionId.DirectoryPages.View,
                    ActionId.DirectoryLocator.View,
                    ActionId.DirectorySuggestions.View,
                    ActionId.DirectoryListingsDuplicate.View,
                    // ActionId.QA.View,
                    ActionId.SearchInsightAnalytics.View,
                    ActionId.DirectoryRichContent.View,
                    ActionId.DirectoryListingPosts.View,
                ],
            },
            upsell: {
                description: 'PRESENCE_UPSELL',
                video: {
                    [LocaleCode.en_US]: 'https://www.youtube.com/embed/NupcjcctrGA',
                    [LocaleCode.de_DE]: 'https://www.youtube.com/embed/VeyhDQFELp4',
                    [LocaleCode.de_CH]: 'https://www.youtube.com/embed/VeyhDQFELp4',
                },
                modules: [
                    ModuleId.AnalyticTraffic,
                    ModuleId.DirectoryListing,
                    ModuleId.DirectoryManaging,
                    ModuleId.DirectoryPublishing,
                    ModuleId.DirectoryLocator,
                    ModuleId.DirectorySuggestions,
                    ModuleId.DirectoryListingDuplicate,
                    ModuleId.DirectoryListingPosts,
                    ModuleId.QAMonitoring,
                    ModuleId.QAResponding,
                    ModuleId.SearchInsightsOrganic,
                ],
            },
            userTypes: [UserType.TenantUser],
            subtabs: [
                {
                    name: 'LISTING_PERFORMANCE',
                    path: `${Route.Listings}/traffic2`,
                    icon: 'icon-chart-bar',
                    auth: {
                        actions: [ActionId.AnalyticTrafficInsights.View],
                    },
                },
                {
                    name: 'LISTING_ACCURACY',
                    path: `${Route.Listings}/accuracy2`,
                    icon: 'icon-folder',
                    auth: {
                        actions: [ActionId.DirectoryListings.View],
                    },
                },
                {
                    name: 'BL_LOCATIONS_DETAILS',
                    path: `${Route.Listings}/details2`,
                    icon: 'icon-map-marker-alt',
                    auth: {
                        actions: [ActionId.DirectoryListings.View],
                    },
                },
                {
                    name: 'PROFILES',
                    path: `${Route.Listings}/profiles`,
                    icon: 'icon-address-card',
                    auth: {
                        actions: [ActionId.DirectoryProfiles.View],
                    },
                },
                {
                    name: 'PUBLISHER_SUGGESTIONS',
                    path: `${Route.Listings}/suggestions`,
                    icon: 'icon-user-edit',
                    auth: {
                        actions: [ActionId.DirectorySuggestions.View],
                    },
                },
                {
                    name: 'LISTING_DUPLICATE',
                    path: `${Route.Listings}/duplicate`,
                    icon: 'icon-analysis',
                    auth: {
                        actions: [ActionId.DirectoryListingsDuplicate.View],
                    },
                },
                {
                    name: 'LISTING_POST',
                    path: `${Route.Listings}/listing-posts`,
                    icon: 'icon-question-circle',
                    auth: {
                        actions: [
                            ActionId.DirectoryListingPosts.View,
                            ActionId.DirectoryListingPosts.Edit,
                            ActionId.DirectoryListingPosts.Add,
                            ActionId.DirectoryListingPosts.Delete,
                        ],
                    },
                },
                {
                    name: 'PAGES',
                    path: `${Route.Listings}/pages`,
                    icon: 'icon-file-alt',
                    auth: {
                        actions: [ActionId.DirectoryPages.View],
                    },
                },
                {
                    name: 'LOCATORS',
                    path: `${Route.Listings}/locator`,
                    icon: 'icon-flag',
                    auth: {
                        actions: [ActionId.DirectoryLocator.View],
                    },
                },
                {
                    name: 'SEARCH_INSIGHT_ANALYTICS',
                    path: `${Route.Insights}/search-ranking`,
                    icon: 'icon-search',
                    auth: {
                        actions: [ActionId.SearchInsightAnalytics.View],
                    },
                },
                {
                    name: 'RICH_CONTENT',
                    path: `${Route.Listings}/rich-content`,
                    icon: 'icon-question-circle',
                    auth: {
                        actions: [ActionId.DirectoryRichContent.View],
                    },
                },
            ],
        },
        {
            name: 'SOCIAL_2',
            path: Route.Social2,
            icon: 'icon-comments-alt',
            auth: {
                actions: [
                    ActionId.SocialPosts.View,
                    ActionId.SocialCampaigns.View,
                    ActionId.SocialCampaignMetrics.View,
                    ActionId.SocialPublishPosts.Add,
                    ActionId.SocialPublishPosts.Approve,
                    ActionId.SocialPublishPosts.Delete,
                    ActionId.SocialPublishPosts.Edit,
                    ActionId.SocialPublishPosts.View,
                    ActionId.SocialRepoContent.View,
                ],
            },
            userTypes: [UserType.TenantUser],
            subtabs: [
                {
                    name: 'SOCIAL_HOME',
                    path: Route.Social2,
                    icon: 'icon-comments',
                    auth: {
                        actions: [
                            ActionId.SocialPosts.View,
                            ActionId.SocialCampaigns.View,
                            ActionId.SocialCampaignMetrics.View,
                            ActionId.SocialPublishPosts.Add,
                            ActionId.SocialPublishPosts.Approve,
                            ActionId.SocialPublishPosts.Delete,
                            ActionId.SocialPublishPosts.Edit,
                            ActionId.SocialPublishPosts.View,
                            ActionId.SocialListeningLivestream.View,
                            ActionId.SocialListeningMetrics.View,
                            ActionId.SocialRepoContent.View,
                            ActionId.SocialMetrics.View,
                            ActionId.AdMetrics.View,
                        ],
                    },
                },
                {
                    name: 'RECENT_POSTS',
                    path: `${Route.Social2}/conversations`,
                    icon: 'icon-list',
                    auth: {
                        actions: [ActionId.SocialPosts.View],
                    },
                },
                {
                    name: 'CAMPAIGNS',
                    path: `${Route.Social2}/campaigns`,
                    icon: 'icon-comment-alt-dots',
                    auth: {
                        actions: [ActionId.SocialCampaigns.View, ActionId.SocialCampaignMetrics.View],
                    },
                },
                {
                    name: 'CALENDAR',
                    path: `${Route.Social2}/calendar2`,
                    icon: 'icon-calendar',
                    auth: {
                        actions: [
                            ActionId.SocialPublishPosts.Add,
                            ActionId.SocialPublishPosts.Approve,
                            ActionId.SocialPublishPosts.Delete,
                            ActionId.SocialPublishPosts.Edit,
                            ActionId.SocialPublishPosts.View,
                        ],
                    },
                },
                {
                    name: 'LISTENING',
                    path: `${Route.Social2}/listening`,
                    icon: 'icon-assistive-listening-systems',
                    auth: {
                        actions: [ActionId.SocialListeningLivestream.View, ActionId.SocialListeningMetrics.View],
                    },
                    upsell: {
                        description: 'SOCIAL_LISTENING_UPSELL',
                        video: 'https://www.youtube.com/embed/pJX1wpfwyKk',
                        modules: [ModuleId.SocialListening],
                    },
                },
                {
                    name: 'LIBRARY',
                    path: `${Route.Social2}/library2`,
                    icon: 'icon-list',
                    auth: {
                        actions: [ActionId.SocialRepoContent.View],
                    },
                },
                {
                    name: 'METRICS',
                    path: `${Route.Social2}/metrics2`,
                    icon: 'icon-chart-bar',
                    auth: {
                        actions: [ActionId.SocialMetrics.View],
                    },
                },
                {
                    name: 'AD_METRICS',
                    path: `${Route.Social}/ad-metrics`,
                    icon: 'icon-chart-bar',
                    auth: {
                        actions: [ActionId.AdMetrics.View],
                    },
                },
            ],
        },
        {
            name: 'LISTENING',
            path: `${Route.Social2}/listening-nuvi?route=social_monitors&domain=${document.domain}`,
            icon: 'icon-assistive-listening-systems',
            auth: {
                actions: [ActionId.ListenMonitors.View],
            },
            upsell: {
                description: 'SOCIAL_LISTENING_UPSELL',
                video: 'https://www.youtube.com/embed/pJX1wpfwyKk',
                modules: [ModuleId.SocialListening],
            },
            userTypes: [UserType.TenantUser],
            subtabs: [
                {
                    name: 'MONITORS',
                    path: `${Route.Social2}/listening-nuvi?route=social_monitors&domain=${document.domain}`,
                    icon: 'icon-browser',
                    auth: {
                        actions: [ActionId.ListenMonitors.View],
                    },
                },
                {
                    name: 'EXPLORE',
                    path: `${Route.Social2}/listening-nuvi?route=listen/explore&domain=${document.domain}`,
                    icon: 'icon-eye',
                    auth: {
                        actions: [ActionId.ListenMonitors.View],
                    },
                },
                {
                    name: 'DASHBOARDS',
                    path: `${Route.Social2}/listening-nuvi?route=listen/dashboards&domain=${document.domain}`,
                    icon: 'icon-tachometer-fast',
                    auth: {
                        actions: [ActionId.ListenDashboards.View],
                    },
                },
                {
                    name: 'REPORTS',
                    path: `${Route.Social2}/listening-nuvi?route=listen/reports&domain=${document.domain}`,
                    icon: 'icon-chart-bar',
                    auth: {
                        actions: [ActionId.ListenReports.View],
                    },
                },
                {
                    name: 'ALERTS',
                    path: `${Route.Social2}/listening-nuvi?route=alerts&domain=${document.domain}`,
                    icon: 'icon-alarm-exclamation',
                    auth: {
                        actions: [ActionId.PublishDashboards.View],
                    },
                },
                {
                    name: 'SEGMENTS',
                    path: `${Route.Social2}/listening-nuvi?route=listen/segments&domain=${document.domain}`,
                    icon: 'icon-shapes',
                    auth: {
                        actions: [ActionId.PublishDashboards.View],
                    },
                },
                {
                    name: 'TAGS',
                    path: `${Route.Social2}/listening-nuvi?route=tags/listen&domain=${document.domain}`,
                    icon: 'icon-tag',
                    auth: {
                        actions: [ActionId.ListenMonitors.View],
                    },
                },
                {
                    name: 'DASHBOARD_TEMPLATES',
                    path: `${Route.Social2}/listening-nuvi?route=listen/dashboard_templates&domain=${document.domain}`,
                    icon: 'icon-folders',
                    auth: {
                        actions: [ActionId.ListenMonitors.View],
                    },
                },
                {
                    name: 'RULE_TEMPLATES',
                    path: `${Route.Social2}/listening-nuvi?route=rule_templates&domain=${document.domain}`,
                    icon: 'icon-exclamation-triangle',
                    auth: {
                        actions: [ActionId.ListenMonitors.View],
                    },
                },
                {
                    name: 'TOP_MENTION_DIGESTS',
                    path: `${Route.Social2}/listening-nuvi?route=digests&domain=${document.domain}`,
                    icon: 'icon-comment-dots',
                    auth: {
                        actions: [ActionId.ListenMonitors.View],
                    },
                },
                {
                    name: 'INTEGRATIONS',
                    path: `${Route.Social2}/listening-nuvi?route=integrations/listen&domain=${document.domain}`,
                    icon: 'icon-lightbulb-on',
                    auth: {
                        actions: [ActionId.ListenMonitors.View],
                    },
                },
                {
                    name: 'SENTIMENT',
                    path: `${Route.Social2}/listening-nuvi?route=sentiment&domain=${document.domain}`,
                    icon: 'icon-comment-dots',
                    auth: {
                        actions: [ActionId.ListenMonitors.View],
                    },
                },
                {
                    name: 'MESSENGERS',
                    path: `${Route.Social2}/listening-nuvi?route=messengers&domain=${document.domain}`,
                    icon: 'icon-comments-alt',
                    auth: {
                        actions: [ActionId.ListenMonitors.View],
                    },
                },
                {
                    name: 'MENTION_USAGE',
                    path: `${Route.Social2}/listening-nuvi?route=mention_usage&domain=${document.domain}`,
                    icon: 'icon-comment-check',
                    auth: {
                        actions: [ActionId.ListenMonitors.View],
                    },
                },
            ],
        },
        {
            name: 'ENGAGE',
            path: `${Route.Social2}/engage-nuvi?route=engage&domain=${document.domain}`,
            icon: 'icon-comments',
            auth: {
                actions: [ActionId.EngageEngagements.View],
            },
            userTypes: [UserType.TenantUser],
            subtabs: [
                {
                    name: 'INBOX',
                    path: `${Route.Social2}/engage-nuvi?route=engage&domain=${document.domain}`,
                    icon: 'icon-calendar',
                    auth: {
                        actions: [ActionId.EngageEngagements.View],
                    },
                },
                {
                    name: 'DASHBOARDS',
                    path: `${Route.Social2}/engage-nuvi?route=engage/dashboards&domain=${document.domain}`,
                    icon: 'icon-tachometer-fast',
                    auth: {
                        actions: [ActionId.EngageDashboards.View],
                    },
                },
                {
                    name: 'REPORTS',
                    path: `${Route.Social2}/engage-nuvi?route=engage/reports&domain=${document.domain}`,
                    icon: 'icon-chart-bar',
                    auth: {
                        actions: [ActionId.EngageReports.View],
                    },
                },
                {
                    name: 'QUEUES',
                    path: `${Route.Social2}/engage-nuvi?route=queues&domain=${document.domain}`,
                    icon: 'icon-exchange-alt',
                    auth: {
                        actions: [ActionId.EngageQueues.View],
                    },
                },
                {
                    name: 'SEGMENTS',
                    path: `${Route.Social2}/engage-nuvi?route=engage/segments&domain=${document.domain}`,
                    icon: 'icon-shapes',
                    auth: {
                        actions: [ActionId.EngageSegments.View],
                    },
                },
                /*  {
                    name: 'INTEGRATIONS',
                    path: `${Route.Social2}/engage-nuvi?route=integrations/engage&domain=${document.domain}`,
                    icon: 'icon-lightbulb-on',
                    auth: {
                        actions: [ActionId.EngageEngagements.View],
                    },
                },  */
                {
                    name: 'TAGS',
                    path: `${Route.Social2}/engage-nuvi?route=tags/engage&domain=${document.domain}`,
                    icon: 'icon-tag',
                    auth: {
                        actions: [ActionId.EngageTags.View],
                    },
                },
                {
                    name: 'RESOLUTIONS_BY_USER',
                    path: `${Route.Social2}/engage-nuvi?route=engage/resolutions_by_user&domain=${document.domain}`,
                    icon: 'icon-user-cog',
                    auth: {
                        actions: [ActionId.EngageEngagements.View],
                    },
                },
                /*  {
                    name: 'REPORT_EMAIL_GROUPS',
                    path: `${Route.Social2}/engage-nuvi?route=messengers&domain=${document.domain}`,
                    icon: 'icon-comments-alt',
                    auth: {
                        actions: [ActionId.EngageEngagements.View],
                    },
                },  */
            ],
        },
        {
            name: 'PUBLISH',
            path: `${Route.Social2}/publish-nuvi?route=post-management&domain=${document.domain}`,
            icon: 'icon-calendar',
            auth: {
                actions: [ActionId.PublishPosts.View],
            },
            userTypes: [UserType.TenantUser],
            subtabs: [
                {
                    name: 'POST_MANAGEMENT',
                    path: `${Route.Social2}/publish-nuvi?route=post-management&domain=${document.domain}`,
                    icon: 'icon-calendar',
                    auth: {
                        actions: [ActionId.PublishPosts.View],
                    },
                },
                {
                    name: 'RECENT_POSTS',
                    path: `${Route.Social2}/publish-nuvi?route=posts&domain=${document.domain}`,
                    icon: 'icon-photo-video',
                    auth: {
                        actions: [ActionId.PublishPosts.View],
                    },
                },
                {
                    name: 'DISCOVER_CONTENT',
                    path: `${Route.Social2}/publish-nuvi?route=discover&domain=${document.domain}`,
                    icon: 'icon-eye',
                    auth: {
                        actions: [ActionId.PublishPosts.View],
                    },
                },
                {
                    name: 'DASHBOARDS',
                    path: `${Route.Social2}/publish-nuvi?route=publish/dashboards&domain=${document.domain}`,
                    icon: 'icon-tachometer-fast',
                    auth: {
                        actions: [ActionId.PublishDashboards.View],
                    },
                },
                {
                    name: 'REPORTS',
                    path: `${Route.Social2}/publish-nuvi?route=publish/reports&domain=${document.domain}`,
                    icon: 'icon-chart-bar',
                    auth: {
                        actions: [ActionId.PublishDashboards.View],
                    },
                },
                {
                    name: 'MEDIA_LIBRARY',
                    path: `${Route.Social2}/publish-nuvi?route=media_gallery&domain=${document.domain}`,
                    icon: 'icon-photo-video',
                    auth: {
                        actions: [ActionId.PublishPosts.View],
                    },
                },
                {
                    name: 'CAMPAIGNS',
                    path: `${Route.Social2}/publish-nuvi?route=campaigns&domain=${document.domain}`,
                    icon: 'icon-comment-alt-dots',
                    auth: {
                        actions: [ActionId.PublishPosts.View],
                    },
                },
                {
                    name: 'MANAGE_LABELS',
                    path: `${Route.Admin}/agencies/${profile?.tenant?.agencyID!}/tenants/${profile?.tenant?.id!}/tags`,
                    icon: 'icon-tag',
                    auth: {
                        actions: [ActionId.PublishPosts.View, ActionId.PlatformTags.View],
                    },
                },
                {
                    name: 'SMART_SOCIAL',
                    path: `${Route.Social2}/publish-nuvi?route=smart_social&domain=${document.domain}`,
                    icon: 'icon-eye',
                    auth: {
                        actions: [ActionId.PublishPosts.View],
                    },
                },
            ],
        },
        {
            name: 'REQUESTS',
            path: Route.Requests,
            icon: 'icon-paper-plane',
            auth: {
                actions: [
                    ActionId.Requests.Send,
                    ActionId.Requests.SendSMS,
                    ActionId.Requests.BulkSend,
                    ActionId.RequestMetrics.View,
                    ActionId.Requests.View,
                    ActionId.RequestTemplates.View,
                ],
            },
            upsell: {
                description: 'REQUESTS_UPSELL',
                video: 'https://player.vimeo.com/video/250224863',
                modules: [ModuleId.RequestManaging],
            },
            userTypes: [UserType.TenantUser],
            subtabs: [
                {
                    name: 'QUICK_REQUEST',
                    path: `${Route.Requests}/quick-request`,
                    icon: 'icon-paper-plane',
                    auth: {
                        actions: [ActionId.Requests.Send, ActionId.Requests.SendSMS],
                    },
                },
                {
                    name: 'BULK_UPLOAD',
                    path: `${Route.Requests}/bulk-emailer`,
                    icon: 'icon-upload',
                    auth: (() => {
                        if (
                            !(
                                profile.isImpersonated &&
                                profile?.impersonatedBy?.agencyId === SUPER_AGENCY_ID &&
                                profile?.user.system
                            ) &&
                            !isAuthorized({ featureFlags: [FeatureFlagId.EnableBulkUpload] })
                        ) {
                            return { featureFlags: [FeatureFlagId.EnableBulkUpload] };
                        }
                        return { actions: [ActionId.Requests.BulkSend] };
                    })(),
                },
                {
                    name: 'LEADERBOARD',
                    path: `${Route.Requests}/leaderboard`,
                    icon: 'icon-star',
                    auth: {
                        actions: [ActionId.RequestLeaderboard.View],
                    },
                },
                {
                    name: 'REQUEST_METRICS',
                    path: `${Route.Requests}/metrics`,
                    icon: 'icon-chart-bar',
                    auth: {
                        actions: [ActionId.RequestMetrics.View],
                    },
                },
                {
                    name: 'REQUEST_HISTORY',
                    path: `${Route.Requests}/history`,
                    icon: 'icon-history',
                    auth: {
                        actions: [ActionId.Requests.View],
                    },
                },
                {
                    name: 'TEMPLATES',
                    path: `${Route.Requests}/templates`,
                    icon: 'icon-code',
                    auth: {
                        actions: [ActionId.RequestTemplates.View],
                    },
                },
            ],
        },
        {
            name: 'SURVEYS_3',
            path: Route.Surveys3,
            icon: 'icon-clipboard-list-check',
            auth: {
                actions: [
                    ActionId.Survey3Results.View,
                    ActionId.Survey3Summaries.View,
                    ActionId.Survey3Templates.View,
                    ActionId.Survey3BasicTemplates.View,
                    ActionId.SurveyLegacyResults.View,
                    ActionId.Survey3ResponseManager.View,
                    ActionId.KioskReviews.View,
                    ActionId.SurveyResults.View,
                    ActionId.SurveySummaries.View,
                    ActionId.SurveyTemplates.View,
                ],
            },
            upsell: {
                description: 'SURVEYS_UPSELL',
                video: {
                    [LocaleCode.en_US]: 'https://www.youtube.com/embed/Ie6kBpHiHqo',
                    [LocaleCode.de_DE]: 'https://www.youtube.com/embed/EEmtjMDfa4I',
                    [LocaleCode.de_CH]: 'https://www.youtube.com/embed/EEmtjMDfa4I',
                },
                modules: [
                    ModuleId.Survey3Monitoring,
                    ModuleId.Survey3Responding,
                    ModuleId.SurveyLegacyMonitoring,
                    ModuleId.SurveyLegacyResponding,
                    ModuleId.KioskMonitoring,
                    ModuleId.KioskResponding,
                    ModuleId.SurveyMonitoring,
                    ModuleId.SurveyResponding,
                ],
            },
            userTypes: [UserType.TenantUser],
            subtabs: [
                {
                    name: 'ALL_SURVEYS',
                    path: Route.Surveys3,
                    icon: 'icon-clipboard-list-check',
                    auth: {
                        actions: [ActionId.Survey3Templates.View, ActionId.Survey3BasicTemplates.View],
                    },
                },
                {
                    name: 'ALL_SURVEYS',
                    path: `${Route.Surveys3}/templates2/all`,
                    icon: 'icon-clipboard-list-check',
                    auth: {
                        actions: [ActionId.Survey3Templates.View, ActionId.Survey3BasicTemplates.View],
                    },
                },
                {
                    name: 'RESULTS_FEED',
                    path: Route.Surveys1,
                    icon: 'icon-list',
                    auth: {
                        actions: [ActionId.SurveyLegacyResults.View],
                    },
                },
                {
                    name: 'UPGRADED_RESULTS_FEED',
                    path: `${Route.Surveys3}/results/feed`,
                    icon: 'icon-list',
                    auth: {
                        actions: [
                            ActionId.Survey3Results.View,
                            ActionId.Survey3Summaries.View,
                            ActionId.SurveyLegacyResults.View,
                        ],
                    },
                },
                {
                    name: 'RESULTS_MANAGER',
                    path: `${Route.Surveys3}/results-manager`,
                    icon: 'icon-bars',
                    auth: {
                        actions: [ActionId.Survey3ResponseManager.View],
                    },
                },
                {
                    name: 'KIOSK',
                    path: Route.Kiosk,
                    icon: 'icon-mobile',
                    auth: {
                        actions: [ActionId.KioskReviews.View],
                    },
                },
                {
                    name: 'SURVEYS_2',
                    path: Route.Surveys2,
                    icon: 'icon-clipboard-list-check',
                    auth: {
                        actions: [
                            ActionId.SurveyResults.View,
                            ActionId.SurveySummaries.View,
                            ActionId.SurveyTemplates.View,
                        ],
                    },
                },
            ],
        },
        {
            name: 'ACTIONS',
            path: Route.Actions,
            icon: 'icon-tasks',
            auth: (() => {
                if (
                    !(
                        isAuthorized({ featureFlags: [FeatureFlagId.TenantActionsReact] }) &&
                        isAuthorized({ featureFlags: [FeatureFlagId.TenantActionsYetiMigration] })
                    )
                ) {
                    return { actions: [ActionId.Tickets.View] };
                }
                return { actions: [] };
            })(),
            upsell: {
                description: 'ACTIONS_UPSELL',
                video: {
                    [LocaleCode.en_US]: 'https://www.youtube.com/embed/bvEvUrCSGp0',
                    [LocaleCode.de_DE]: 'https://www.youtube.com/embed/qdYKaD8nJa4',
                    [LocaleCode.de_CH]: 'https://www.youtube.com/embed/qdYKaD8nJa4',
                },
                modules: [ModuleId.TicketBasic],
            },
            userTypes: [UserType.TenantUser],
        },
        {
            name: 'ACTIONS',
            path: Route.TenantActions,
            icon: 'icon-tasks',
            auth: (() => {
                if (
                    isAuthorized({ featureFlags: [FeatureFlagId.TenantActionsReact] }) &&
                    isAuthorized({ featureFlags: [FeatureFlagId.TenantActionsYetiMigration] })
                ) {
                    return { actions: [ActionId.Tickets.View] };
                }
                return { actions: [] };
            })(),
            upsell: {
                description: 'ACTIONS_UPSELL',
                video: {
                    [LocaleCode.en_US]: 'https://www.youtube.com/embed/bvEvUrCSGp0',
                    [LocaleCode.de_DE]: 'https://www.youtube.com/embed/qdYKaD8nJa4',
                    [LocaleCode.de_CH]: 'https://www.youtube.com/embed/qdYKaD8nJa4',
                },
                modules: [ModuleId.TicketBasic],
            },
            userTypes: [UserType.TenantUser],
        },
        {
            name: 'ACTION_PLANS',
            path: `${Route.ActionPlans}/dashboard`,
            icon: 'icon-action-plans',
            auth: {
                actions: [ActionId.ActionPlans.View],
                featureFlags: [FeatureFlagId.ActionPlansReact],
            },
            userTypes: [UserType.TenantUser],
            beta: true,
        },
        {
            name: 'INSIGHTS',
            path: Route.Operations,
            icon: 'icon-lightbulb',
            auth: {
                actions: [
                    ActionId.AnalyticWordCloud.View,
                    ActionId.AnalyticTornado.View,
                    ActionId.AnalyticInsights.View,
                ],
            },
            upsell: {
                description: 'OPERATIONS_UPSELL',
                video: 'https://www.youtube.com/embed/d1EjngdoIe4',
                modules: [ModuleId.AnalyticOperating, ModuleId.AnalyticOperatingPlus],
            },
            userTypes: [UserType.TenantUser],
            subtabs: (() => {
                const insightsReactLink = {
                    name: 'EXPERIENCE_INSIGHTS',
                    path: `${Route.Experience}/insights`,
                    icon: 'icon-analysis',
                    auth: {
                        actions: [ActionId.AnalyticInsights.View],
                    },
                };

                const journeyInsightLink = {
                    name: 'JOURNEY_INSIGHTS',
                    path: `${Route.Experience}/journey-insights`,
                    icon: 'icon-analysis',
                    auth: {
                        actions: [
                            ActionId.AnalyticJourneyInsights.View,
                            ActionId.AnalyticWordCloud.View,
                            ActionId.AnalyticTornado.View,
                        ],
                    },
                };

                const aiReputationManagerLink = {
                    name: 'AI_REPUTATION_MANAGER',
                    path: `${Route.Operations}/ai-reputation-manager`,
                    icon: 'icon-analysis',
                    auth: {
                        actions: [
                            {
                                actions: [
                                    ActionId.AnalyticARMResponseMonitoring.Add,
                                    ActionId.AnalyticARMResponseMonitoringUsers.Add,
                                    ActionId.AnalyticARMResponseInsights.View,
                                    ActionId.AnalyticARMResponseMonitoring.View,
                                    ActionId.AnalyticARMResponseMonitoringUsers.View,
                                ],
                            },
                            {
                                actions: [
                                    ActionId.DirectoryProfiles.Add,
                                    ActionId.DirectoryProfiles.Add,
                                    ActionId.DirectoryProfiles.Add,
                                ],
                            },
                        ],
                        mode: 'all',
                    },
                };

                const aiLocationPresenceLink = {
                    name: 'AI_LOCATION_PRESENCE',
                    path: `${Route.Social2}/listening-nuvi?route=ai-location-presence&domain=${document.domain}`,
                    icon: 'icon-analysis',
                    auth: (() => {
                        if (
                            !isAuthorized({
                                actions: [
                                    ActionId.AnalyticARMPresenceDashboard.View,
                                    ActionId.AnalyticARMPresenceDashboard.Add,
                                ],
                            })
                        ) {
                            return {
                                actions: [
                                    ActionId.AnalyticARMPresenceDashboard.View,
                                    ActionId.AnalyticARMPresenceDashboard.Add,
                                ],
                            };
                        }
                        return {
                            actions: [
                                ActionId.DirectoryProfiles.View,
                                ActionId.DirectoryProfiles.Edit,
                                ActionId.DirectoryProfiles.Add,
                            ],
                        };
                    })(),
                };

                const vobLink = {
                    name: 'VOICE_OF_BRAND',
                    path: `${Route.Social2}/listening-nuvi?route=vob/dashboard&domain=${document.domain}`,
                    icon: 'icon-analysis',
                    auth: {
                        actions: [ActionId.AnalyticVOBReviewRespondingDashboard.View],
                    },
                };

                const links: any[] = [insightsReactLink, journeyInsightLink];

                const isCompassModuleEnabled =
                    tenant?.moduleIDs?.includes(ModuleId.AnalyticCompetitiveInsightsCompassStandard) ||
                    tenant?.moduleIDs?.includes(ModuleId.AnalyticCompetitiveInsightsCompassPremium);

                if (isCompassModuleEnabled) {
                    const compassLink = {
                        name: 'COMPETITIVE_INSIGHTS_COMPASS',
                        path: `${Route.Social2}/listening-nuvi?route=competitive-intelligence/location_comparison&domain=${document.domain}`,
                        icon: 'icon-winner',
                        auth: {
                            actions: [
                                ActionId.AnalyticCompetitiveInsightsCompassLocationComparison.Add,
                                ActionId.AnalyticCompetitiveInsightsCompassLocationComparison.Delete,
                                ActionId.AnalyticCompetitiveInsightsCompassLocationComparison.Edit,
                                ActionId.AnalyticCompetitiveInsightsCompassLocationComparison.View,
                                ActionId.AnalyticCompetitiveInsightsCompassBrandComparison.Add,
                                ActionId.AnalyticCompetitiveInsightsCompassBrandComparison.Delete,
                                ActionId.AnalyticCompetitiveInsightsCompassBrandComparison.Edit,
                                ActionId.AnalyticCompetitiveInsightsCompassBrandComparison.View,
                                ActionId.AnalyticCompetitiveInsightsCompassExperienceAnalysis.View,
                                ActionId.AnalyticCompetitiveInsightsCompassCommentExploration.View,
                                ActionId.AnalyticCompetitiveInsightsCompassJourneyInsights.View,
                                ActionId.AnalyticCompetitiveInsightsCompassSWOTReviews.View,
                                ActionId.AnalyticCompetitiveInsightsCompassSWOTWebSources.View,
                            ],
                        },
                    };

                    links.push(compassLink);
                }

                const showAIReputationManagerLink = areModulesEnabled([
                    ModuleId.DirectoryManaging,
                    ModuleId.AnalyticAIReputationManager,
                ]);

                const showAILocationPresenceLink = true;

                if (showAIReputationManagerLink) {
                    links.push(aiReputationManagerLink);
                }

                if (showAILocationPresenceLink) {
                    links.push(aiLocationPresenceLink);
                }

                links.push(vobLink);

                return links;
            })(),
        },
        {
            name: 'REP_SCORE_2',
            path: hasScore2 ? `${Route.Score2}` : `${Route.Score2}/`,
            icon: 'icon-thermometer-quarter',
            auth: {
                actions: [ActionId.ScoreBasicScore.View, ActionId.ScorePrescriptiveScore.View],
            },
            userTypes: [UserType.TenantUser],
            subtabs: (() => {
                const scoreLink = {
                    name: 'REP_SCORE_2',
                    path: hasScore2 ? `${Route.Score2}` : `${Route.Score2}/`,
                    icon: 'icon-thermometer-quarter',
                    auth: {
                        actions: [ActionId.ScoreBasicScore.View, ActionId.ScorePrescriptiveScore.View],
                    },
                };

                const rsImpactReportLink = {
                    name: 'RS_IMPACT_REPORT_DEPARTMENT_LISTINGS_REPORT',
                    path: `${Route.RSImpactReport}`,
                    icon: 'icon-thermometer-quarter',
                    auth: {
                        actions: [
                            ActionId.ScoreBasicScore.View,
                            ActionId.ScorePrescriptiveScore.View,
                            ActionId.RepScoreImpactReport.View,
                        ],
                    },
                };
                const enableRSImpactReportLink = isAuthorized({
                    actions: [ActionId.RepScoreImpactReport.View],
                });

                if ((supportsDepartments || profile.isAgencyUser) && enableRSImpactReportLink) {
                    return [scoreLink, rsImpactReportLink];
                }
                return undefined;
            })(),
        },
        {
            name: 'REPORTS',
            path: Route.Reports,
            icon: 'icon-chart-bar',
            auth: {
                actions: [ActionId.AnalyticReports.View],
            },
            userTypes: [UserType.TenantUser],
            subtabs: (() => {
                if (
                    isAuthorized({
                        featureFlags: [FeatureFlagId.Reports2],
                    })
                ) {
                    return [
                        {
                            name: 'REPORTS',
                            path: Route.Reports,
                            icon: 'icon-tachometer-fast',
                            auth: {
                                actions: [ActionId.AnalyticReports.View],
                            },
                        },
                        {
                            name: 'REPORTS_2',
                            path: Route.Reports2,
                            icon: 'icon-tachometer-fast',
                            auth: {
                                actions: [ActionId.AnalyticReports.View],
                            },
                            beta: true,
                        },
                    ];
                }
                return undefined;
            })(),
        },
        {
            name:
                hasModule(tenant, ModuleId.MessagingInbox) ||
                hasModule(tenant, ModuleId.SocialInbox) ||
                hasModule(tenant, ModuleId.ReviewInbox) ||
                hasModule(tenant, ModuleId.SurveyInbox) ||
                hasModule(tenant, ModuleId.MessagingCampaigns)
                    ? 'INBOX'
                    : 'MESSAGING',
            path: `${Route.Inbox}`,
            icon: 'icon-inbox',
            auth: {
                actions: [
                    ActionId.MessagingInboxConversations.View,
                    ActionId.SocialInboxConversations.View,
                    ActionId.ReviewInboxConversations.View,
                    ActionId.SurveyInboxConversations.View,
                ],
            },
            userTypes: [UserType.TenantUser],
            subtabs: [
                {
                    name: 'INBOX',
                    path: `${Route.Inbox}/conversations`,
                    icon: 'icon-inbox',
                    auth: {
                        actions: [
                            ActionId.MessagingInboxConversations.View,
                            ActionId.SocialInboxConversations.View,
                            ActionId.ReviewInboxConversations.View,
                            ActionId.SurveyInboxConversations.View,
                        ],
                        allowSystemUser: true,
                    },
                },
                {
                    name: 'CAMPAIGNS',
                    path: `${Route.Inbox}/campaigns`,
                    icon: 'icon-inbox',
                    auth: {
                        actions: [ActionId.MessagingCampaigns.View],
                        allowSystemUser: true,
                    },
                },
            ],
            upsell: {
                description: 'INBOX_UPSELL',
                video: {
                    [LocaleCode.en_US]: 'https://www.youtube.com/embed/pEpKSUCWS88',
                    [LocaleCode.de_DE]: 'https://www.youtube.com/embed/g1O8k4fz_T8',
                    [LocaleCode.de_CH]: 'https://www.youtube.com/embed/g1O8k4fz_T8',
                },
                modules: [
                    ModuleId.MessagingInbox,
                    ModuleId.SocialInbox,
                    ModuleId.ReviewInbox,
                    ModuleId.SurveyInbox,
                    ModuleId.MessagingCampaigns,
                ],
            },
        },
        {
            name: 'RESOURCES',
            path: '/resources',
            icon: 'icon-question-circle',
            auth: {
                actions: [ActionId.PlatformUI.Login],
            },
            userTypes: [UserType.TenantUser],
            subtabs: (() => {
                const subtabsList: Tab[] = [
                    {
                        name: 'SUPPORT_PORTAL',
                        path: 'https://support.reputation.com',
                        icon: 'icon-question-circle',
                        auth: {
                            actions: [ActionId.PlatformUI.Login],
                        },
                        newTab: true,
                    },
                    {
                        name: 'REP_ROCKSTARS_COMMUNITY',
                        path: 'https://rockstars.reputation.com/',
                        icon: 'icon-bold',
                        auth: {
                            actions: [ActionId.PlatformUI.Login],
                        },
                        newTab: true,
                    },
                    {
                        name: 'IDEA_PORTAL',
                        path: `${Route.IdeaPortal}`,
                        icon: 'icon-lightbulb',
                        auth: {
                            actions: [ActionId.PlatformUI.Login],
                        },
                        newTab: true,
                    },
                    {
                        name: 'REPUTATION_NEWS',
                        path: 'https://reputation.com/reputation-news',
                        icon: 'icon-address-card',
                        auth: {
                            actions: [ActionId.PlatformUI.Login],
                        },
                        newTab: true,
                    },
                ];

                if (!profile.isImpersonated) {
                    subtabsList.splice(2, 0, {
                        name: 'PLATFORM_EDUCATION',
                        path: '',
                        icon: 'icon-award',
                        auth: {
                            actions: [ActionId.PlatformUI.Login],
                        },
                        newTab: true,
                        onClick: () => {
                            getUserSkillJarLink(profile);
                        },
                    });
                }
                return subtabsList;
            })(),
        },
        {
            name: 'ADMIN',
            path: Route.Admin,
            icon: 'icon-user-cog',
            auth: {
                actions: [ActionId.PlatformTenants.View],
            },
            userTypes: [UserType.AgencyUser],
        },
        {
            name: 'SOURCES',
            path: `${Route.Admin}/sources`,
            icon: 'icon-folder-tree',
            auth: {
                actions: [ActionId.PlatformPrivilegedSources.View],
            },
            userTypes: [UserType.AgencyUser],
        },
        {
            name: 'REPORTS',
            path: `${Route.Admin}/reports`,
            icon: 'icon-chart-bar',
            auth: {
                actions: [ActionId.PlatformPrivilegedReports.View, ActionId.DirectoryListings.View],
                mode: 'all',
            },
            userTypes: [UserType.AgencyUser],
            subtabs: [
                {
                    name: 'LISTINGS_DASHBOARD',
                    path: `${Route.Admin}/reports/listings`,
                    icon: 'icon-list',
                    auth: {
                        actions: [ActionId.PlatformPrivilegedReports.View, ActionId.DirectoryListings.View],
                        mode: 'all',
                    },
                },
                {
                    name: 'AGENCY_DASHBOARD',
                    path: `${Route.Admin}/reports/agency`,
                    icon: 'icon-list',
                    auth: {
                        actions: [ActionId.PlatformPrivilegedReports.View, ActionId.DirectoryListings.View],
                        mode: 'all',
                    },
                },
            ],
        },
        {
            name: 'SEARCH',
            path: `${Route.Admin}/users`,
            icon: 'icon-search',
            auth: {
                actions: [ActionId.PlatformUsers.View],
            },
            userTypes: [UserType.AgencyUser],
        },
        {
            name: 'SOCIAL-CURATION',
            path: Route.SocialCuration,
            icon: 'icon-comments-alt',
            auth: {
                actions: [
                    // ActionId.SocialCurationQueues.View,
                    ActionId.SocialCurationTasks.View,
                ],
            },
            userTypes: [UserType.AgencyUser],
        },
        {
            name: 'DEMO_TENANT',
            path: Route.DemoTenant,
            icon: 'icon-city',
            auth: {
                actions: [ActionId.PlatformPrivilegedTenants.ViewDemo],
            },
            userTypes: [UserType.AgencyUser],
        },
        {
            name: 'TASKS',
            path: Route.Tasks,
            icon: 'icon-tasks',
            auth: {
                actions: [ActionId.Tasks.View],
            },
            userTypes: [UserType.AgencyUser],
            subtabs: [
                {
                    name: 'TASKS',
                    path: `${Route.Tasks}/summaries`,
                    icon: 'icon-list',
                    auth: {
                        actions: [ActionId.Tasks.View],
                    },
                },
                {
                    name: 'RESPONSES',
                    path: `${Route.Tasks}/responses`,
                    icon: 'icon-comment-edit',
                    auth: {
                        actions: [ActionId.Tasks.View],
                    },
                },
                {
                    name: 'BUSINESS_LISTINGS_FULFILLMENT',
                    path: `${Route.Tasks}/listings-fulfillment`,
                    icon: 'icon-hand-pointer',
                    auth: {
                        actions: [ActionId.Tasks.View],
                    },
                },
                {
                    name: 'ACTIONS',
                    path: Route.Actions,
                    icon: 'icon-file-alt',
                    auth: {
                        actions: [ActionId.Tickets.View, ActionId.TicketPrivilegedTickets.View],
                    },
                },
            ],
        },
        {
            name: 'Looker',
            path: Route.Looker,
            icon: 'icon-chart-bar',
            auth: {
                featureFlags: [FeatureFlagId.Looker],
            },
            userTypes: [UserType.TenantUser],
        },
        {
            name: 'ACTIONS',
            beta: true,
            path: `${Route.Actions}/overview2`,
            icon: 'icon-actions',
            auth: {
                actions: [
                    ActionId.AgencyActions.Engineer,
                    ActionId.AgencyActions.ReviewAdmin,
                    ActionId.AgencyActions.ReviewAgent,
                    ActionId.AgencyActions.BLAdmin,
                    ActionId.AgencyActions.BLAgent,
                ],
            },
            userTypes: [UserType.AgencyUser],
        },
    ];
};

interface NavTabLinkProps {
    navTab: NavTab;
    isNavOpen: boolean;
    activePath: string;
}

const getUserSkillJarLink = (profile: Profile): void => {
    (async (): Promise<any> => {
        try {
            const sourceResult = await trackPromise(
                getSkillJarLink(profile?.tenant?.agencyID!, Number(profile?.tenant?.id), profile?.user.id!),
            );

            if (sourceResult?.url) {
                window.open(sourceResult.url, '_blank');
            }
        } catch {}
    })();
};

const isUpsellAuthorized = (profile: Profile, navTab: Tab): boolean => {
    const tabAuthorized = isAuthorized(navTab.auth);

    const hasNoModules = (modules: string[]): boolean => {
        let hasModules = false;

        modules.forEach((module) => {
            if (profile!.modules[module]) {
                hasModules = true;
            }
        });

        return !hasModules;
    };

    if (tabAuthorized) {
        return true;
    }

    // Tab upsell enabled + User has no access + Tenant has no modules
    if (navTab.upsell && hasNoModules(navTab.upsell.modules)) {
        // Disabled tenants
        if (profile.tenant && [43401, 127718].includes(profile.tenant.id)) {
            return false;
        }

        // User has declined
        if (profile.user.preferences && profile.user.preferences[`upsell-${navTab.name}`] === 'false') {
            return false;
        }

        // Show as upsell
        navTab.isUpsell = true; // eslint-disable-line  no-param-reassign
        return true;
    }

    return false;
};

const NavTabItem: FunctionComponent<NavTabLinkProps> = (props) => {
    const { isNavOpen, navTab } = props;
    const tooltipOffset = { modifiers: [{ name: 'offset', options: { offset: [0, 20] } }] };

    return !isNavOpen ? (
        <OverlayTrigger
            placement="right"
            popperConfig={tooltipOffset}
            overlay={
                <Tooltip id={`navTabsTooltip-${navTab.name}`}>
                    <NavTabString name={navTab.name} beta={navTab.beta} />
                </Tooltip>
            }
        >
            <i className={navTab.icon} />
        </OverlayTrigger>
    ) : (
        <i className={navTab.icon} />
    );
};

const NavTabLink: FunctionComponent<NavTabLinkProps> = (props) => {
    const { activePath, isNavOpen, navTab } = props;
    const itemClassnames = classnames('r4e-left-nav-tab', {
        'left-nav-tab-state-closed': !isNavOpen,
    });
    // const [showUpsellPicker, setShowUpsellPicker] = useState(false);

    const isActive = Boolean(navTab.path && (activePath === navTab.path || activePath.startsWith(`${navTab.path}/`)));

    return (
        <NavItem role="listitem" key={navTab.path} className={itemClassnames}>
            {!navTab.isUpsell && (
                <AppLink
                    aria-labelledby={`left-nav-link-${navTab.name}`}
                    path={navTab.path!}
                    className={classnames('nav-link', { active: isActive })}
                >
                    <NavTabItem {...props} />
                    <NavTabHtml name={navTab.name} beta={navTab.beta} />
                </AppLink>
            )}
            {/* {navTab.isUpsell && (
                <NavLink
                    aria-labelledby={`left-nav-link-${navTab.name}`}
                    className="upsell"
                    onClick={(e: MouseEvent): void => {
                        setShowUpsellPicker(true);
                    }}
                >
                    <NavTabItem {...props} />
                    <NavTabHtml name={navTab.name} beta={navTab.beta} />

                   
                    <div onClick={(e: MouseEvent): void => e.stopPropagation()}>
                        <UpsellPicker
                            tab={navTab}
                            show={showUpsellPicker}
                            onClose={(e: MouseEvent): void => {
                                setShowUpsellPicker(false);
                            }}
                        />
                    </div>
                    
                </NavLink>
            )} */}
        </NavItem>
    );
};

const NavTabLinkWithSubTabs: FunctionComponent<NavTabLinkProps> = (props) => {
    const [{ profile }] = useProfile();
    const { activePath, isNavOpen, navTab } = props;
    const [isHovered, setIsHovered] = useState(false);
    const itemClassnames = classnames('r4e-left-nav-tab', {
        'left-nav-tab-state-closed': !isNavOpen,
    });
    const subTabPaths = navTab.subtabs ? navTab.subtabs.map((subtab) => subtab.path) : [];
    // const [showUpsellPicker, setShowUpsellPicker] = useState(false);
    const parentRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    const isAnySubTabActive = subTabPaths.some(
        (subPath) => subPath && (activePath === subPath || activePath.startsWith(`${subPath}/`)),
    );

    // nav-link & active = bootstrap classnames
    const toggleClassnames = classnames('nav-link', {
        active: isHovered || isAnySubTabActive,
    });

    useEffect(() => {
        const handleMouseMove = (e: globalThis.MouseEvent) => {
            handleMouseLeave(e);
        };

        window.addEventListener('mousemove', handleMouseMove);
        return () => window.removeEventListener('mousemove', handleMouseMove);
    }, []);

    const handleMouseLeave = (e: globalThis.MouseEvent): void => {
        if (!parentRef.current || !menuRef.current) {
            setIsHovered(false);
            return;
        }

        const parentRect = parentRef.current.getBoundingClientRect();
        const menuRect = menuRef.current.getBoundingClientRect();

        const eventX = e.clientX;
        const eventY = e.clientY;

        const insideParent =
            eventX >= parentRect.left &&
            eventX <= parentRect.right &&
            eventY >= parentRect.top &&
            eventY <= parentRect.bottom;

        const insideMenu =
            eventX >= menuRect.left && eventX <= menuRect.right && eventY >= menuRect.top && eventY <= menuRect.bottom;

        if (insideParent || insideMenu) {
            return;
        }

        // Mouse truly left both areas → close menu
        setIsHovered(false);
    };

    return (
        <Dropdown
            onMouseEnter={(): void => setIsHovered(true)}
            onFocus={(): void => setIsHovered(true)}
            show={isHovered}
            as={NavItem}
            key={navTab.path}
            drop="right"
            role="listitem"
            className={itemClassnames}
            ref={parentRef}
            name={navTab.name}
        >
            <Dropdown.Toggle aria-labelledby={`left-nav-link-${navTab.name}`} as={NavLink} className={toggleClassnames}>
                <>
                    <i className={navTab.icon} />
                    <NavTabHtml name={navTab.name} beta={navTab.beta} />
                </>
            </Dropdown.Toggle>
            <Dropdown.Menu
                popperConfig={{
                    strategy: 'fixed',
                }}
                ref={menuRef}
            >
                <Dropdown.Header>
                    <NavTabString name={navTab.name} beta={navTab.beta} />
                </Dropdown.Header>
                {navTab.subtabs
                    ?.filter((subTab) => isUpsellAuthorized(profile!, subTab))
                    .map((subTab) => {
                        if (subTab.isUpsell) {
                            return (
                                <></>
                                // <Dropdown.Item
                                //     key={subTab.path}
                                //     as={NavLink}
                                //     className="upsell"
                                //     onClick={(e: MouseEvent): void => {
                                //         setShowUpsellPicker(true);
                                //     }}
                                // >
                                //     <NavTabHtml name={subTab.name} beta={subTab.beta} />
                                //     <div onClick={(e: MouseEvent): void => e.stopPropagation()}>
                                //         <UpsellPicker
                                //             tab={subTab}
                                //             show={showUpsellPicker}
                                //             onClose={(e: MouseEvent): void => {
                                //                 setShowUpsellPicker(false);
                                //             }}
                                //         />
                                //     </div>
                                // </Dropdown.Item>
                            );
                        }

                        if (subTab.onClick) {
                            return (
                                <Dropdown.Item
                                    key={subTab.path}
                                    as={AppLink}
                                    onClick={subTab.onClick}
                                    newTab={subTab.newTab}
                                >
                                    <NavTabHtml name={subTab.name} beta={subTab.beta} />
                                </Dropdown.Item>
                            );
                        }

                        return (
                            <Dropdown.Item key={subTab.path} as={AppLink} path={subTab.path} newTab={subTab.newTab}>
                                <NavTabHtml name={subTab.name} beta={subTab.beta} />
                            </Dropdown.Item>
                        );
                    })}
            </Dropdown.Menu>
        </Dropdown>
    );
};

interface Props {
    isNavOpen: boolean;
}

export const NavTabs: FunctionComponent<Props> = ({ isNavOpen }) => {
    const [{ profile }] = useProfile();

    const [navtabs, setNavtabs] = useState<NavTab[] | undefined>();

    const { pathname: activePath } = useLocation();

    // Add custom tabs
    useEffect(() => {
        (async (): Promise<void> => {
            if (profile && !navtabs) {
                try {
                    if (isNumber(get(profile, 'tenant.id') && !profile.isAgencyUser)) {
                        const resp = await getDepartmentHealthSupport(profile.tenant?.id, { filters: [] }, {});

                        if (resp && get(resp, 'supportsDepartments', false) === true) {
                            supportsDepartments = true;
                        }
                    }
                } catch (err) {}
                let completeNavtabs = getNavTabs(profile);

                if (profile.tenant && profile.tenant.menus?.length) {
                    const customNavTab: NavTab = {
                        name: 'MORE',
                        path: Route.More,
                        icon: 'icon-ellipsis-h',
                        auth: {
                            actions: [ActionId.PlatformCustomTabs.View],
                        },
                        userTypes: [UserType.TenantUser],
                        subtabs: [],
                    };

                    profile.tenant.menus.forEach((menu) => {
                        customNavTab.subtabs!.push({
                            name: menu.name,
                            path: menu.url,
                            icon: '',
                            auth: {
                                actions: [ActionId.PlatformCustomTabs.View],
                            },
                        });
                    });

                    completeNavtabs.push(customNavTab);
                }

                const [
                    isSurveys3AllSurveysV2Enabled,
                    isSurveys3ResultsFeedV2Enabled,
                    isSurveys3ResultsFeedV2BetaRolloutEnabled,
                    isListingsCompetitiveInsightsEnabled,
                    user,
                    isListingPostsEnabled,
                ] = await trackPromise(
                    Promise.all([
                        isFeatureFlagEnabled(Number(profile?.tenant?.id), FeatureFlagId.Surveys3AllSurveysV2),
                        isFeatureFlagEnabled(Number(profile?.tenant?.id), FeatureFlagId.Surveys3ResultsFeedV2),
                        isFeatureFlagEnabled(Number(profile?.tenant?.id), FeatureFlagId.Surveys3ResultsBetaRollout),
                        hasModule(profile.tenant, ModuleId.SearchRankingAdvance) ||
                            hasModule(profile.tenant, ModuleId.SearchRankingBasic) ||
                            hasModule(profile.tenant, ModuleId.SearchRankingPremium),
                        getUser(profile?.tenant?.agencyID!, Number(profile?.tenant?.id || -1), profile?.user.id!),
                        hasModule(profile.tenant, ModuleId.DirectoryListingPosts),
                    ]),
                );

                const surveysTab: NavTab | undefined = completeNavtabs.find((tab) => tab.path === Route.Surveys3);

                if (isSurveys3AllSurveysV2Enabled.enabled) {
                    const legacyAllSurveysTab: any = surveysTab?.subtabs?.findIndex(
                        (tab: any) => tab.path === Route.Surveys3,
                    );

                    surveysTab?.subtabs?.splice(legacyAllSurveysTab, 1);
                } else {
                    const allSurveysV2Tab: any = surveysTab?.subtabs?.findIndex(
                        (tab: any) => tab.path === `${Route.Surveys3}/templates2/all`,
                    );

                    surveysTab?.subtabs?.splice(allSurveysV2Tab, 1);
                }

                const hasNewResultsFeedAccess = user?.preferences?.hasNewResultsFeedAccess;
                const hasResultsFeedExclusive = profile?.tenant?.moduleIDs?.includes(ModuleId.Survey3ResultsFeedUpdate);

                if (
                    (isSurveys3ResultsFeedV2BetaRolloutEnabled.enabled &&
                        (!hasNewResultsFeedAccess || hasNewResultsFeedAccess === 'true')) ||
                    hasResultsFeedExclusive
                ) {
                    const surveysTab: any = completeNavtabs.find((tab) => tab.path === Route.Surveys3);
                    const legacyResultsFeedTabIndex: any = surveysTab.subtabs?.findIndex(
                        (tab: any) => tab.path === Route.Surveys1,
                    );

                    surveysTab.subtabs?.splice(legacyResultsFeedTabIndex, 1);
                }

                if (
                    (isSurveys3ResultsFeedV2BetaRolloutEnabled.enabled && hasNewResultsFeedAccess === 'false') ||
                    (!isSurveys3ResultsFeedV2Enabled.enabled &&
                        !isSurveys3ResultsFeedV2BetaRolloutEnabled.enabled &&
                        !hasResultsFeedExclusive)
                ) {
                    const surveysTab: any = completeNavtabs.find((tab) => tab.path === Route.Surveys3);
                    const RFV2TabIndex: any = surveysTab.subtabs?.findIndex(
                        (tab: any) => tab.path === `${Route.Surveys3}/results/feed`,
                    );

                    surveysTab.subtabs?.splice(RFV2TabIndex, 1);
                }

                try {
                    const isKioskMigrationEnabled = await isFeatureFlagEnabled(
                        Number(profile?.tenant?.id),
                        FeatureFlagId.KioskSurveyMigration,
                    );

                    if (isKioskMigrationEnabled.enabled) {
                        const surveysTab: any = completeNavtabs.find((tab) => tab.path === Route.Surveys3);
                        const kioskTabIndex: any = surveysTab?.subtabs?.findIndex(
                            (tab: any) => tab.path === Route.Kiosk,
                        );

                        if (kioskTabIndex !== -1) {
                            surveysTab.subtabs?.splice(kioskTabIndex, 1);
                        }
                    }
                } catch (error) {}

                if (isListingsCompetitiveInsightsEnabled) {
                    const competitiveInsightsTab: NavTab = {
                        name: 'RANKING_INSIGHTS',
                        path: `${Route.Listings}/local-search-insights`,
                        icon: 'icon-chart-bar',
                        auth: {
                            actions: [ActionId.AnalyticTrafficInsights.View],
                        },
                        userTypes: [],
                    };

                    const listingTabs: any = completeNavtabs.find((tab) => tab.path === Route.Listings);

                    listingTabs.subtabs?.splice(1, 0, competitiveInsightsTab);
                }

                const listingsTab: any = completeNavtabs.find((tab) => tab.path === Route.Listings);
                const locatorSubTab: any = listingsTab.subtabs.find((subTab: any) => subTab.name === 'LOCATORS');
                const pagesSubTab: any = listingsTab.subtabs.find((subTab: any) => subTab.name === 'PAGES');

                locatorSubTab.path = `${Route.Listings}/locator2`;
                pagesSubTab.path = `${Route.Listings}/pages2`;

                if (!isListingPostsEnabled) {
                    const listingTabs: any = completeNavtabs.find((tab) => tab.path === Route.Listings);

                    listingTabs.subtabs = listingTabs.subtabs?.filter(
                        (tab: NavTab) => tab.path !== `${Route.Listings}/listing-posts`,
                    );
                }
                // Resources
                // Disable for Rent.com
                if (profile.tenant && [176094].includes(profile.tenant.id)) {
                    completeNavtabs = completeNavtabs.filter((tab) => tab.name !== 'RESOURCES');
                }

                if (profile.featureFlags[FeatureFlagId.NovaCommandCenter]) {
                    completeNavtabs = completeNavtabs.filter((tab) => tab.name !== 'DASHBOARDS');
                }

                const resourcesTab = completeNavtabs.find((tab) => tab.name === 'RESOURCES');

                if (resourcesTab) {
                    // For EU/RU, remove Repuation News and Academy when user is not of us/gb english locale
                    if (
                        (window.location.href.includes('app-eu') || window.location.href.includes('app-ru')) &&
                        ![LocaleCode.en_US, LocaleCode.en_GB].includes(profile.user.locale)
                    ) {
                        resourcesTab.subtabs = resourcesTab.subtabs!.filter(
                            (subTab) => subTab.name !== 'REPUTATION_NEWS',
                        );
                    }
                    // Remove Idea Portal for impersonated users & non US customers
                    if (profile.isImpersonated || window.location.href.includes('app-ru')) {
                        resourcesTab.subtabs = resourcesTab.subtabs!.filter((subTab) => subTab.name !== 'IDEA_PORTAL');
                    }

                    // Remove Reputation News for GM, Ford and Nissan
                    if (profile.tenant && [11743, 289, 43401].includes(profile.tenant.id)) {
                        resourcesTab.auth = {
                            featureFlags: [FeatureFlagId.ResourcesLinks],
                        };
                        resourcesTab.subtabs = resourcesTab.subtabs!.filter(
                            (subTab) => subTab.name !== 'REPUTATION_NEWS',
                        );
                    }
                }

                setNavtabs(completeNavtabs);
            }
        })();
    }, [profile, navtabs, setNavtabs]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!profile || !navtabs) {
        return null;
    }

    return (
        <Nav role="list" style={{ overflowY: 'auto' }}>
            {navtabs
                .filter((navTab) => isUpsellAuthorized(profile!, navTab))
                .filter((navTab) => !navTab.disabled)
                .filter((navTab) =>
                    navTab.userTypes.includes(profile.isAgencyUser ? UserType.AgencyUser : UserType.TenantUser),
                )
                .map((navTab) => {
                    if (navTab.subtabs && !navTab.isUpsell) {
                        return (
                            <NavTabLinkWithSubTabs
                                activePath={activePath}
                                key={navTab.path}
                                isNavOpen={isNavOpen}
                                navTab={navTab}
                            />
                        );
                    }

                    return (
                        <NavTabLink key={navTab.path} isNavOpen={isNavOpen} navTab={navTab} activePath={activePath} />
                    );
                })}
        </Nav>
    );
};
